import { jsPDF } from 'jspdf';

// Cache for the loaded font
let fontLoaded = false;
let fontData: string | null = null;

// Load Roboto font from Google Fonts CDN (supports UTF-8 including German umlauts)
// Note: jsPDF requires TTF format, not WOFF
async function loadRobotoFont(): Promise<string | null> {
  if (fontData) return fontData;
  
  try {
    // Load Roboto Regular TTF from a reliable CDN
    const response = await fetch('https://cdn.jsdelivr.net/fontsource/fonts/roboto@latest/latin-400-normal.ttf');
    if (!response.ok) {
      console.warn('Failed to load Roboto font, falling back to default');
      return null;
    }
    
    const arrayBuffer = await response.arrayBuffer();
    
    // Convert ArrayBuffer to base64
    const bytes = new Uint8Array(arrayBuffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    fontData = btoa(binary);
    
    return fontData;
  } catch (error) {
    console.warn('Error loading font:', error);
    return null;
  }
}

async function loadRobotoBoldFont(): Promise<string | null> {
  try {
    // Load Roboto Bold TTF from a reliable CDN
    const response = await fetch('https://cdn.jsdelivr.net/fontsource/fonts/roboto@latest/latin-700-normal.ttf');
    if (!response.ok) {
      return null;
    }
    
    const arrayBuffer = await response.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  } catch (error) {
    console.warn('Error loading bold font:', error);
    return null;
  }
}

/**
 * Initialize jsPDF with UTF-8 compatible fonts
 * Call this before using the document for text with special characters
 */
export async function initializePdfWithUtf8Font(doc: jsPDF): Promise<boolean> {
  if (fontLoaded) {
    // Font already registered in this session, just set it
    try {
      doc.setFont('Roboto', 'normal');
      return true;
    } catch {
      return false;
    }
  }
  
  try {
    const [regularFont, boldFont] = await Promise.all([
      loadRobotoFont(),
      loadRobotoBoldFont()
    ]);
    
    if (regularFont) {
      // Add font to Virtual File System (TTF format required by jsPDF)
      doc.addFileToVFS('Roboto-Regular.ttf', regularFont);
      doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
      
      if (boldFont) {
        doc.addFileToVFS('Roboto-Bold.ttf', boldFont);
        doc.addFont('Roboto-Bold.ttf', 'Roboto', 'bold');
      }
      
      // Set as default font
      doc.setFont('Roboto', 'normal');
      fontLoaded = true;
      return true;
    }
    
    return false;
  } catch (error) {
    console.warn('Failed to initialize UTF-8 font:', error);
    return false;
  }
}

/**
 * Get the font name to use - returns 'Roboto' if loaded, otherwise 'helvetica'
 */
export function getUtf8FontName(): string {
  return fontLoaded ? 'Roboto' : 'helvetica';
}

/**
 * Check if UTF-8 font is loaded
 */
export function isUtf8FontLoaded(): boolean {
  return fontLoaded;
}
